export const ROW_COLORS = ['red', 'yellow', 'green', 'blue'];

export const ROWS = Object.freeze({
  red: Object.freeze([2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  yellow: Object.freeze([2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  green: Object.freeze([12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2]),
  blue: Object.freeze([12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2])
});

export const MAX_PENALTIES = 4;
export const PENALTY_POINTS = 5;
export const LOCKS_TO_END = 2;
export const MIN_MARKS_BEFORE_LOCK = 5;

export function createEmptyBoard() {
  return {
    rows: {
      red: { marked: [], lock: false },
      yellow: { marked: [], lock: false },
      green: { marked: [], lock: false },
      blue: { marked: [], lock: false }
    },
    penalties: 0
  };
}

export function rollDie(random = Math.random) {
  return Math.floor(random() * 6) + 1;
}

export function rollAllDice(random = Math.random, lockedRows = {}) {
  const dice = {
    white1: rollDie(random),
    white2: rollDie(random),
    red: null,
    yellow: null,
    green: null,
    blue: null
  };

  for (const color of ROW_COLORS) {
    if (!lockedRows[color]) dice[color] = rollDie(random);
  }

  return dice;
}

export function whiteTotal(dice) {
  return dice.white1 + dice.white2;
}

export function rowIndex(color, value) {
  return ROWS[color]?.indexOf(value) ?? -1;
}

export function rightmostMarkedIndex(board, color) {
  const marked = board.rows[color].marked;
  if (!marked.length) return -1;
  return Math.max(...marked.map((value) => rowIndex(color, value)));
}

export function validateMark(board, color, value, lockedRows = {}) {
  if (!ROW_COLORS.includes(color)) return { ok: false, reason: 'unknown-color' };
  if (lockedRows[color]) return { ok: false, reason: 'row-locked' };

  const targetIndex = rowIndex(color, value);
  if (targetIndex < 0) return { ok: false, reason: 'value-not-in-row' };
  if (board.rows[color].marked.includes(value)) return { ok: false, reason: 'already-marked' };
  if (targetIndex <= rightmostMarkedIndex(board, color)) return { ok: false, reason: 'must-move-right' };

  const isFinalNumber = targetIndex === ROWS[color].length - 1;
  if (isFinalNumber && board.rows[color].marked.length < MIN_MARKS_BEFORE_LOCK) {
    return { ok: false, reason: 'not-enough-marks-to-lock' };
  }

  return { ok: true, locksRow: isFinalNumber };
}

export function applyMark(board, color, value, lockedRows = {}) {
  const validation = validateMark(board, color, value, lockedRows);
  if (!validation.ok) return validation;

  board.rows[color].marked.push(value);
  board.rows[color].marked.sort((a, b) => rowIndex(color, a) - rowIndex(color, b));
  if (validation.locksRow) board.rows[color].lock = true;

  return { ok: true, locksRow: validation.locksRow };
}

export function addPenalty(board) {
  board.penalties = Math.min(MAX_PENALTIES, board.penalties + 1);
  return board.penalties;
}

export function availablePublicMoves(board, total, lockedRows = {}) {
  return ROW_COLORS
    .filter((color) => validateMark(board, color, total, lockedRows).ok)
    .map((color) => ({ color, value: total }));
}

export function availableColoredMoves(board, dice, lockedRows = {}) {
  const moves = [];
  const seen = new Set();

  for (const color of ROW_COLORS) {
    const colorDie = dice[color];
    if (colorDie == null || lockedRows[color]) continue;

    for (const whiteKey of ['white1', 'white2']) {
      const value = colorDie + dice[whiteKey];
      const key = `${color}:${value}`;
      if (seen.has(key)) continue;
      seen.add(key);

      if (validateMark(board, color, value, lockedRows).ok) {
        moves.push({ color, value, whiteKey, colorDie, whiteDie: dice[whiteKey] });
      }
    }
  }

  return moves;
}

export function recommendMoves(board, moves = [], maxSkipped = 2) {
  if (!moves.length) return [];
  const scored = moves.map((move) => ({
    move,
    skipped: Math.max(0, rowIndex(move.color, move.value) - rightmostMarkedIndex(board, move.color) - 1),
    locks: rowIndex(move.color, move.value) === ROWS[move.color].length - 1
      && board.rows[move.color].marked.length >= MIN_MARKS_BEFORE_LOCK
  }));
  const usefulLocks = scored.filter((entry) => entry.locks && entry.skipped <= maxSkipped + 1);
  if (usefulLocks.length) {
    const lockSkip = Math.min(...usefulLocks.map((entry) => entry.skipped));
    return usefulLocks.filter((entry) => entry.skipped === lockSkip).map((entry) => entry.move);
  }
  const fewestSkipped = Math.min(...scored.map((entry) => entry.skipped));
  // Do not turn a legal but wasteful early jump into advice. For example,
  // opening an empty row with 7 discards five future fields; passing is better.
  if (fewestSkipped > maxSkipped) return [];
  return scored.filter((entry) => entry.skipped === fewestSkipped).map((entry) => entry.move);
}

export function triangularScore(markCount) {
  return (markCount * (markCount + 1)) / 2;
}

export function scoreRow(row) {
  return triangularScore(row.marked.length + (row.lock ? 1 : 0));
}

export function scoreBoard(board) {
  const rows = Object.fromEntries(
    ROW_COLORS.map((color) => [color, scoreRow(board.rows[color])])
  );
  const subtotal = Object.values(rows).reduce((sum, value) => sum + value, 0);
  const penalty = board.penalties * PENALTY_POINTS;

  return {
    rows,
    subtotal,
    penalty,
    total: subtotal - penalty
  };
}

export function isGameOver(players, lockedRows = {}) {
  const lockCount = ROW_COLORS.filter((color) => Boolean(lockedRows[color])).length;
  const penaltyPlayer = players.find((player) => player.board.penalties >= MAX_PENALTIES);

  if (lockCount >= LOCKS_TO_END) return { over: true, reason: 'two-rows-locked' };
  if (penaltyPlayer) return { over: true, reason: 'four-penalties', playerId: penaltyPlayer.id };
  return { over: false };
}

export function rankPlayers(players) {
  const sorted = players
    .map((player) => ({
      id: player.id,
      name: player.name,
      isBot: Boolean(player.isBot),
      score: scoreBoard(player.board)
    }))
    .sort((a, b) => b.score.total - a.score.total || a.name.localeCompare(b.name, 'de'));

  let previousScore = null;
  let previousRank = 0;
  return sorted.map((entry, index) => {
    const rank = previousScore !== null && entry.score.total === previousScore ? previousRank : index + 1;
    previousScore = entry.score.total;
    previousRank = rank;
    return { ...entry, rank };
  });
}

export function sanitizeName(input, fallback = 'Spieler') {
  const name = String(input ?? '')
    .replace(/[<>\u0000-\u001f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 24);
  return name || fallback;
}

export function sanitizeRoomCode(input) {
  return String(input ?? '')
    .replace(/\D/g, '')
    .slice(0, 6);
}

export function chooseBotPublicMove(board, total, lockedRows = {}, random = Math.random) {
  const moves = availablePublicMoves(board, total, lockedRows);
  if (!moves.length) return null;

  const scored = moves.map((move) => {
    const index = rowIndex(move.color, move.value);
    const skipped = index - rightmostMarkedIndex(board, move.color) - 1;
    const locks = index === ROWS[move.color].length - 1;
    const marked = board.rows[move.color].marked.length;
    const desirability = (locks ? 100 : 0) + marked * 3 - skipped * 2 + random();
    return { move, desirability };
  });

  scored.sort((a, b) => b.desirability - a.desirability);
  const best = scored[0];

  // Bots occasionally pass on wasteful early jumps.
  const jump = rowIndex(best.move.color, best.move.value) - rightmostMarkedIndex(board, best.move.color) - 1;
  if (jump >= 3 && board.rows[best.move.color].marked.length < 3 && random() < 0.8) return null;
  return best.move;
}

export function chooseBotColoredMove(board, dice, lockedRows = {}, random = Math.random) {
  const moves = availableColoredMoves(board, dice, lockedRows);
  if (!moves.length) return null;

  const scored = moves.map((move) => {
    const index = rowIndex(move.color, move.value);
    const skipped = index - rightmostMarkedIndex(board, move.color) - 1;
    const locks = index === ROWS[move.color].length - 1;
    const marked = board.rows[move.color].marked.length;
    return {
      move,
      desirability: (locks ? 120 : 0) + marked * 4 - skipped * 2.5 + random()
    };
  });

  scored.sort((a, b) => b.desirability - a.desirability);
  const best = scored[0];
  const jump = rowIndex(best.move.color, best.move.value) - rightmostMarkedIndex(board, best.move.color) - 1;
  if (jump >= 3 && board.rows[best.move.color].marked.length < 3 && random() < 0.7) return null;
  return best.move;
}
